import type { SettingDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { defineRxdbCollectionModel } from './migration';

// ============================================================================
// RxDB collection schema for settings (user_settings + feature flags).
//
// The D1 `user_settings` table uses an integer auto-increment PK, which is
// not suitable for RxDB. We synthesize a string primary key: `${userId}:${type}`
// since (userId, type) is effectively unique in practice.
//
// Push handler on the server silently drops feature-flag writes (server is
// authoritative for featureFlag / feature-flags). Pull combines userSettings
// rows + synthetic feature-flag docs from the users table.
// ============================================================================

export const SETTINGS_COLLECTION = 'settings' as const;
export const SETTINGS_SCHEMA_VERSION = 0;

export const settingsSchemaLiteral = {
  title: 'settings schema',
  version: SETTINGS_SCHEMA_VERSION,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 200 },   // `${userId}:${type}`
    userId: { type: 'string', maxLength: 100 },
    type: { type: 'string', enum: ['feature-flags', 'onboarding', 'preferences'] },
    value: { type: 'object' },
    updatedAt: { type: 'string', maxLength: 30 },
  },
  required: ['id', 'userId', 'type', 'value', 'updatedAt'],
  indexes: [['userId', 'updatedAt']],
} as const;

export interface SettingsCheckpoint {
  updatedAt: string;
  id: string;
}

type _SettingSchemaDocType = Omit<ExtractDocumentTypeFromTypedRxJsonSchema<typeof settingsSchemaLiteral>, 'value'> & {
  value: SettingDocType['value'];
};
type _SettingDocTypeInSync = Assert<AssertEqual<_SettingSchemaDocType, SettingDocType>>;

export const settingsSchema: RxJsonSchema<SettingDocType> = settingsSchemaLiteral;
export const settingsModel = defineRxdbCollectionModel<typeof SETTINGS_COLLECTION, SettingDocType>({
  collection: SETTINGS_COLLECTION,
  schema: settingsSchema,
});
